import React from 'react'
import { useMediaQuery } from '@mui/material'
import PropTypes from 'prop-types'

import Sections from '@templates/strapi-cms/content-types/Banner/BannerSections'
import { generateImgData, generateKey, getImageData, getRowHeight, getItemRatio, calculateTotalRows } from './helpers'

import {
  ImageListStyles,
  ImageListItemStyles,
  InnerHtmlStyles,
  StrapiLinkSubtitleStyles,
  StrapiLinkTileStyles,
  SectionStyles,
  ImageListItemBarStyles,
  StrapiImageStyles,
  GridStyles,
  StrapiButtonStyles,
} from './MosaicGrid.styles'

const justify = {
  Left: 'flex-start',
  Center: 'center',
  Right: 'flex-end',
  SpaceAround: 'space-around',
  SpaceEvenly: 'space-evenly',
}

const align = {
  Top: 'flex-start',
  Middle: 'center',
  Bottom: 'flex-end',
}

const MosaicGrid = ({ mosaicItems, mosaicId, mosaicTitle, displaySettings, mosaicGridButtons }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'))
  const hasTileL = mosaicItems.find(item => item?.MosaicGridTile?.TileColumnsAndRows === 'TileL_Columns9_Rows9')
  const itemRatio = getItemRatio(hasTileL)
  const totalRows = displaySettings !== 'Desktop' ? calculateTotalRows(mosaicItems, displaySettings, itemRatio) : 0

  return (
    <ImageListStyles variant="quilted" rows={totalRows}>
      {mosaicItems.map((item, index) => {
        const {
          TileLink,
          Image,
          TileColumnsAndRows,
          BackgroundColor,
          FontColor,
          Heading,
          DisplayLinkText,
          Buttons,
          ButtonsBackgroundColor,
          ButtonsFontColor,
          ButtonsBorderColor,
          BackgroundColorHover,
          TextColorHover,
          Alignment,
          VerticalAlignment,
          ButtonsSpacing,
          ButtonsHorizontalSpacing,
          ButtonSize,
          TileSections,
        } = item?.MosaicGridTile || {}
        const itemCols = itemRatio[TileColumnsAndRows]?.cols
        const itemRows = itemRatio[TileColumnsAndRows]?.rows
        const headingHtml = Heading?.data?.childMarkdownRemark?.html
        const topMargin = ButtonsSpacing?.TopPadding || mosaicGridButtons?.spacing?.TopPadding
        const bottomMargin = ButtonsSpacing?.BottomPadding || mosaicGridButtons?.spacing?.BottomPadding
        const leftMargin = ButtonsHorizontalSpacing?.LeftPadding || mosaicGridButtons?.horizontalSpacing?.LeftPadding
        const rightMargin = ButtonsHorizontalSpacing?.RightPadding || mosaicGridButtons?.horizontalSpacing?.RightPadding
        const buttonContainerProps = {
          justifyContent: justify[Alignment] || justify[mosaicGridButtons?.alignment] || null,
          alignItems: align[VerticalAlignment] || align[mosaicGridButtons?.verticalAlignment] || 'flex-end',
        }
        const buttonProps = {
          backgroundColor: ButtonsBackgroundColor?.ColorHex || mosaicGridButtons?.backgroundColor,
          color: ButtonsFontColor?.ColorHex || mosaicGridButtons?.color,
          borderColor: ButtonsBorderColor?.ColorHex || mosaicGridButtons?.borderColor,
          mosaicGridBackgroundColorHover: BackgroundColorHover?.ColorHex || mosaicGridButtons?.backgroundColorHover,
          mosaicGridTextColorHover: TextColorHover?.ColorHex || mosaicGridButtons?.textColorHover,
          margin: `${topMargin || 0}px ${rightMargin || 0}px ${bottomMargin || 0}px ${leftMargin || 0}px`,
        }

        return (
          <ImageListItemStyles
            key={generateKey(mosaicId, TileColumnsAndRows, isMobile, index)}
            itemrationame={TileColumnsAndRows}
            itemratio={itemRatio?.[TileColumnsAndRows]}
            cols={itemCols}
            rows={itemRows}
            rowheight={getRowHeight(isMobile, itemRows)}
            backgroundcolor={BackgroundColor?.ColorHex}
            isSection={TileSections}
          >
            {Image && <StrapiImageStyles data={generateImgData(getImageData(Image), mosaicTitle)} noPaddingBottom />}
            {TileSections?.length > 0 && <Sections data={TileSections} isMosaicGridTile />}
            {TileSections?.length === 0 && (
              <ImageListItemBarStyles
                sx={{ background: 'transparent' }}
                title={
                  <MosaicGridTitleSection
                    heading={Heading}
                    tileLink={TileLink}
                    headingHtml={headingHtml}
                    fontcolor={FontColor}
                    displaylinktext={DisplayLinkText}
                    tileButtons={Buttons}
                    buttonContainerProps={buttonContainerProps}
                    buttonProps={buttonProps}
                    isSection={TileSections}
                    buttonSize={ButtonSize || mosaicGridButtons?.buttonSize}
                  />
                }
                position="top"
              />
            )}
          </ImageListItemStyles>
        )
      })}
    </ImageListStyles>
  )
}

const MosaicGridTitleSection = ({
  heading,
  tileLink,
  headingHtml,
  fontcolor,
  displaylinktext,
  tileButtons,
  buttonContainerProps,
  buttonProps,
  isSection,
  buttonSize,
}) => (
  <SectionStyles>
    {heading && <InnerHtmlStyles fontcolor={fontcolor} html={headingHtml} />}
    {(tileLink || tileButtons[0]?.Button?.Link?.Link) && (
      <>
        {displaylinktext !== false && (
          <StrapiLinkSubtitleStyles fontcolor={fontcolor}>
            {tileLink?.DisplayText || tileButtons[0]?.Button?.Link?.Link?.DisplayText}
          </StrapiLinkSubtitleStyles>
        )}
        {isSection.length === 0 && <StrapiLinkTileStyles data={tileLink || tileButtons[0]?.Button?.Link?.Link} />}
      </>
    )}
    {tileButtons.length > 0 && (
      <GridStyles {...buttonContainerProps}>
        {tileButtons.map(btn => (
          <StrapiButtonStyles
            key={btn?.Button?.testId}
            data={btn?.Button}
            buttonBackgroundColor={buttonProps.backgroundColor}
            buttonColor={buttonProps.color}
            styles={{ ...buttonProps }}
            buttonSize={buttonSize}
          />
        ))}
      </GridStyles>
    )}
  </SectionStyles>
)

MosaicGrid.propTypes = {
  mosaicItems: PropTypes.array,
  mosaicId: PropTypes.string,
  mosaicTitle: PropTypes.string,
  displaySettings: PropTypes.string,
  mosaicGridButtons: PropTypes.object,
}

MosaicGridTitleSection.propTypes = {
  heading: PropTypes.object,
  tileLink: PropTypes.object,
  headingHtml: PropTypes.string,
  fontcolor: PropTypes.object,
  displaylinktext: PropTypes.bool,
  tileButtons: PropTypes.array,
  buttonContainerProps: PropTypes.object,
  buttonProps: PropTypes.object,
  isSection: PropTypes.bool,
  buttonSize: PropTypes.string,
}

export default MosaicGrid
